<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { PayoutAccountStatus } from '@/types/payouts'

type BannerVariant = 'setup' | 'action_required' | 'restricted'

const { t, locale } = useI18n()
const route = useRoute()
const { currentOrganisation } = useAuth()

// Only owners can act on payout setup, so only owners see the banner. It is
// deliberately not dismissible and hidden on the Payments tab itself, which
// carries the same message in more detail.
const variant = computed<BannerVariant | null>(() => {
  const org = currentOrganisation.value
  if (org === null || org.role !== 'owner') return null
  if (route.name === 'organizer.organisations.settings.payments') return null

  switch (org.payout_account?.status) {
    case PayoutAccountStatus.NotStarted:
    case PayoutAccountStatus.Incomplete:
      return 'setup'
    case PayoutAccountStatus.ActionRequired:
      return 'action_required'
    case PayoutAccountStatus.Restricted:
      return 'restricted'
    default:
      return null
  }
})

const dueDate = computed<string | null>(() => {
  const iso = currentOrganisation.value?.payout_account?.requirements_due_at
  if (!iso) return null
  return new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium' }).format(new Date(iso))
})

const text = computed<string>(() => {
  switch (variant.value) {
    case 'setup':
      return t('payouts.banner.setup_text')
    case 'action_required':
      return dueDate.value !== null
        ? t('payouts.banner.action_required_text_due', { date: dueDate.value })
        : t('payouts.banner.action_required_text')
    case 'restricted':
      return t('payouts.banner.restricted_text')
    default:
      return ''
  }
})

const linkLabel = computed<string>(() =>
  variant.value === 'setup' ? t('payouts.banner.setup_link') : t('payouts.banner.update_link'),
)

const containerClass = computed<string>(() =>
  variant.value === 'restricted'
    ? 'bg-red-500/10 border-red-500/30'
    : 'bg-amber-500/10 border-amber-500/30',
)

const textClass = computed<string>(() => (variant.value === 'restricted' ? 'text-red-300' : 'text-amber-300'))
const linkClass = computed<string>(() =>
  variant.value === 'restricted'
    ? 'text-red-300 hover:text-red-200'
    : 'text-amber-300 hover:text-amber-200',
)
</script>

<template>
  <div
    v-if="variant !== null && currentOrganisation !== null"
    class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-6 pb-0"
    data-testid="payout-account-banner"
  >
    <div
      class="rounded-xl border px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      :class="containerClass"
      :role="variant === 'restricted' ? 'alert' : 'status'"
      :data-variant="variant"
    >
      <p class="text-sm" :class="textClass">{{ text }}</p>
      <RouterLink
        :to="{ name: 'organizer.organisations.settings.payments', params: { orgSlug: currentOrganisation.slug } }"
        class="shrink-0 text-sm font-semibold transition-colors"
        :class="linkClass"
      >
        {{ linkLabel }}
      </RouterLink>
    </div>
  </div>
</template>
